/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  FlexContainer,
  Input,
  Label,
  List,
  Message,
  Spacer,
  Switch,
  Text,
  theme,
  Upload,
} from "@nordcloud/gnui";
import { ValidationError } from "~/generated/graphql";
import { ExternalLink, FormGroup, FormWrap } from "~/components";
import { showError } from "~/services/toast";
import { isNotEmpty } from "~/tools";
import { StepContainer } from "~/views/accounts/AddAccount/components/StepContainer";
import { hasFiles } from "~/views/accounts/AddAccount/GcpAccountForm/helpers";
import { GoogleJsonData } from "~/views/accounts/AddAccount/GcpAccountForm/types";
import { readFile } from "~/views/cloudAccounts/utils";

enum FormField {
  ORGANIZATION_ID = "organization_id",
  SERVICE_ACCOUNT_KEY = "service_account_key",
  PROJECT_NAME = "project_name",
  BIGQUERY_DATASET_NAME = "bigquery_dataset_name",
  BIGQUERY_TABLE_NAME = "bigquery_table_name",
  ADVANCED_BILLING_DATA = "advanced_billing_data",
}

type FormData = {
  [FormField.ORGANIZATION_ID]: string;
  [FormField.SERVICE_ACCOUNT_KEY]: FileList;
  [FormField.PROJECT_NAME]: string;
  [FormField.BIGQUERY_DATASET_NAME]: string;
  [FormField.BIGQUERY_TABLE_NAME]: string;
  [FormField.ADVANCED_BILLING_DATA]: boolean;
};

type SubmitData = Omit<FormData, FormField.SERVICE_ACCOUNT_KEY> & {
  [FormField.SERVICE_ACCOUNT_KEY]: GoogleJsonData;
};

const UPLOAD_PLACEHOLDER_TEXT = "Upload service account key as .json file";
const ERROR_READ_FILE = "Cannot read file contents";

type Props = {
  onSubmit: SubmitHandler<SubmitData>;
  onPrevStep: () => void;
  validationErrors?: ValidationError[];
};

export function GcpForm({
  onSubmit,
  onPrevStep,
  validationErrors = [],
}: Props) {
  const [filename, setFilename] = React.useState("");
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onChange",
  });

  const handleUpload = async (data: FormData) => {
    try {
      const json = await readFile<GoogleJsonData>(data.service_account_key[0]);

      onSubmit({
        ...data,
        service_account_key: json,
      });
    } catch {
      showError(ERROR_READ_FILE);
      setFilename("");
      reset();
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    callback: (e: unknown) => void
  ) => {
    const fileList = e.target.files;
    if (fileList instanceof FileList && hasFiles(fileList)) {
      setFilename(fileList[0].name);
    }

    callback(fileList);
  };

  const watcher = watch();

  const values = Object.values(watcher);
  // TODO: verify if needed for PLaywright
  // Custom isValid implementation due to problems with file uploading inside Cypress tests
  const isValid =
    isNotEmpty(values) &&
    values.every((value) => {
      if (value instanceof FileList) {
        return value.length > 0;
      }
      if (typeof value === "string") {
        return isNotEmpty(value ?? "");
      }
      return true;
    });

  const disableSubmit = !isValid || isNotEmpty(Object.keys(errors));
  const uploadPlaceholder = isNotEmpty(filename)
    ? filename
    : UPLOAD_PLACEHOLDER_TEXT;

  const generalFormErrors = validationErrors.filter(
    ({ field }) => field === null || field === ""
  );
  const serviceAccountKeyErrors = validationErrors.filter(
    ({ field }) => field?.includes("serviceAccountKey.")
  );

  return (
    <FormWrap
      footerProps={{
        nextButtonProps: { disabled: disableSubmit },
        onPrevClick: onPrevStep,
      }}
      onSubmit={handleSubmit(handleUpload)}
    >
      <FlexContainer wrap="wrap" direction="column" alignItems="stretch">
        <StepContainer counter={1}>
          <Text>
            Before exporting your Cloud Billing Data, you must set up a project
            to contain your BigQuery dataset.
          </Text>
        </StepContainer>
        <StepContainer counter={2}>
          <Text>
            To export your Cloud Billing Data, follow these instructions:
            <br />
            <ExternalLink href="https://cloud.google.com/billing/docs/how-to/export-data-bigquery">
              https://cloud.google.com/billing/docs/how-to/export-data-bigquery
            </ExternalLink>
          </Text>
        </StepContainer>
        <StepContainer counter={3}>
          <Text>
            Create a Service Account and set the following roles in that Service
            Account project:
          </Text>
          <List
            unordered
            css={{ paddingLeft: 0, marginLeft: "-6px" }}
            items={[
              {
                title: "BigQuery Data Viewer",
              },
              {
                title: "BigQuery User",
              },
            ]}
          />
        </StepContainer>
        <StepContainer counter={4}>
          <Text>Add this information to access your billing data:</Text>
          {generalFormErrors.map(({ error }) => (
            <Message key={error} status="danger" image="danger">
              {error}
            </Message>
          ))}
          <Spacer height={theme.spacing.spacing04} />
          <FormGroup error={errors[FormField.ORGANIZATION_ID]}>
            <Label
              required
              htmlFor={FormField.ORGANIZATION_ID}
              name="Organization ID"
            />
            <Input
              id={FormField.ORGANIZATION_ID}
              // @ts-expect-error ignored temporary after migration to TypeScript 4.9
              {...register(FormField.ORGANIZATION_ID, {
                required: "This field is required.",
                minLength: {
                  value: 3,
                  message: "Minimum 3 characters required",
                },
              })}
            />
          </FormGroup>
          <FormGroup error={errors[FormField.SERVICE_ACCOUNT_KEY]}>
            <Label
              required
              htmlFor={FormField.SERVICE_ACCOUNT_KEY}
              name="Service Account Key"
            />
            <Controller
              name={FormField.SERVICE_ACCOUNT_KEY}
              control={control}
              rules={{ required: "This field is required." }}
              render={({ field: { onChange } }) => (
                <Upload
                  id={FormField.SERVICE_ACCOUNT_KEY}
                  placeholder={uploadPlaceholder}
                  onChange={(e) => handleInputChange(e, onChange)}
                />
              )}
            />
          </FormGroup>
          {serviceAccountKeyErrors.map(({ error, field }) => (
            <Message
              key={error}
              css={{ marginBottom: theme.spacing.spacing02 }}
              status="danger"
              image="danger"
            >
              {field?.split(".")[1]}: {error}
            </Message>
          ))}
          <FormGroup error={errors[FormField.PROJECT_NAME]}>
            <Label
              required
              htmlFor={FormField.PROJECT_NAME}
              name="Project Name"
            />
            <Input
              id={FormField.PROJECT_NAME}
              {...register(FormField.PROJECT_NAME, {
                required: "This field is required.",
                minLength: {
                  value: 3,
                  message: "Minimum 3 characters required",
                },
              })}
            />
          </FormGroup>

          <FormGroup error={errors[FormField.BIGQUERY_DATASET_NAME]}>
            <Label
              required
              htmlFor={FormField.BIGQUERY_DATASET_NAME}
              name="BigQuery Dataset Name"
            />
            <Input
              id={FormField.BIGQUERY_DATASET_NAME}
              {...register(FormField.BIGQUERY_DATASET_NAME, {
                required: "This field is required.",
                minLength: {
                  value: 3,
                  message: "Minimum 3 characters required",
                },
              })}
            />
          </FormGroup>
          <FormGroup error={errors[FormField.BIGQUERY_TABLE_NAME]}>
            <Label
              required
              htmlFor={FormField.BIGQUERY_TABLE_NAME}
              name="BigQuery Table Name"
            />
            <Input
              id={FormField.BIGQUERY_TABLE_NAME}
              {...register(FormField.BIGQUERY_TABLE_NAME, {
                required: "This field is required.",
                minLength: {
                  value: 3,
                  message: "Minimum 3 characters required",
                },
              })}
            />
          </FormGroup>
          <FormGroup error={errors[FormField.ADVANCED_BILLING_DATA]}>
            <Controller
              name={FormField.ADVANCED_BILLING_DATA}
              control={control}
              render={({ field: { onChange } }) => (
                <Switch
                  id={FormField.ADVANCED_BILLING_DATA}
                  labelText="Use Advanced Billing Data"
                  data-testid="advanced-billing-data"
                  onChange={onChange}
                />
              )}
            />
          </FormGroup>
        </StepContainer>
      </FlexContainer>
    </FormWrap>
  );
}
